// ── 출퇴근 수정 폼 (편집 모달 내부 콘텐츠) — 데스크탑 2열 컴팩트 + 지각 사유 ──
import React from 'react';
import {
  getTotalMinutesByHHmm, getAutoBreakMinutes, formatMinutesKorean, formatIpWithAlias,
} from './utils';
import { finalizeTime } from '../../../utils/timeInput';
import { inputStyle, fieldStyle, labelStyle, editFormStyle, registeredBoxStyle, registeredListStyle } from './styles';

// 지각/조퇴/미체크/휴무 사유 박스 (읽기 전용) — 모달 상단 강조
function StatusSummary({ row }) {
  if (!row) return null;
  const lateMin = Number(row.late_minutes || 0);
  const earlyMin = Number(row.early_leave_minutes || 0);
  const isMissingOut = Boolean(row.check_in) && !row.check_out;
  const isDayoff = Boolean(row.is_dayoff);
  const isOffdayWork = Boolean(row.is_offday_work);
  const items = [];
  if (lateMin > 0) items.push({ text: `지각 ${lateMin}분`, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' });
  if (earlyMin > 0) items.push({ text: `조퇴 ${earlyMin}분`, color: '#fb923c', bg: 'rgba(251,146,60,0.12)', border: 'rgba(251,146,60,0.3)' });
  if (isMissingOut) items.push({ text: '퇴근 미체크', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)' });
  if (isDayoff) items.push({ text: '휴무일', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.3)' });
  if (isOffdayWork) items.push({ text: '휴무근무', color: '#15803d', bg: 'rgba(21,128,61,0.12)', border: 'rgba(21,128,61,0.3)' });
  if (items.length === 0) items.push({ text: '정상', color: '#0f766e', bg: 'rgba(15,118,110,0.12)', border: 'rgba(15,118,110,0.3)' });
  return (
    <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((it, i) => (
        <span key={i} style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, color: it.color, background: it.bg, border: `1px solid ${it.border}` }}>{it.text}</span>
      ))}
    </div>
  );
}

/**
 * 출퇴근 수정 모달의 폼 내부 콘텐츠
 * Props:
 *   form                     — 폼 필드 객체 { date, check_in_time, ip_address, check_in_address, check_out_time, check_out_ip_address, check_out_address, break_minutes }
 *   setForm                  — 폼 상태 setter
 *   patchTimeForm(field, value) — 시간 필드 변경 + 자동 휴게시간 업데이트
 *   breakManuallyEdited      — 사용자가 직접 휴게시간을 입력했는지 여부
 *   setBreakManuallyEdited   — 위 상태 setter
 *   editingRegisteredIps     — 편집 중인 직원의 등록 IP 목록
 */
export function AttendanceEditForm({
  form, setForm, patchTimeForm,
  breakManuallyEdited, setBreakManuallyEdited,
  editingRegisteredIps, editingRow,
}) {
  // 데스크탑 2열 그리드 + 모바일 1열 자동 (auto-fit)
  const gridStyle = { ...editFormStyle, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 };
  // 컴팩트 padding (모달 한 화면)
  const compactInput = { ...inputStyle, padding: '6px 10px', fontSize: 13 };
  return (
    <div style={gridStyle}>
      {/* 상태 요약 (지각/조퇴/미체크/휴무 등) — 전체 폭 */}
      <StatusSummary row={editingRow} />

      {/* 날짜 */}
      <div style={fieldStyle}>
        <label style={labelStyle}>날짜</label>
        <input
          style={compactInput} type="date" value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
      </div>

      {/* 출근 시간 */}
      <div style={fieldStyle}>
        <label style={labelStyle}>출근 시간 (HH:mm)</label>
        <input
          style={compactInput} type="text" inputMode="numeric" placeholder="0930 또는 09:30"
          value={form.check_in_time}
          onChange={(e) => patchTimeForm('check_in_time', e.target.value)}
          onBlur={(e) => patchTimeForm('check_in_time', finalizeTime(e.target.value))}
        />
      </div>

      {/* 출근 IP */}
      <div style={fieldStyle}>
        <label style={labelStyle}>출근 IP</label>
        <input
          style={compactInput} value={form.ip_address}
          onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
        />
      </div>

      {/* 출근 주소 — 전체 폭 (긴 텍스트) */}
      <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
        <label style={labelStyle}>출근 주소</label>
        <input
          style={compactInput} value={form.check_in_address}
          onChange={(e) => setForm({ ...form, check_in_address: e.target.value })}
        />
      </div>

      {/* 퇴근 시간 */}
      <div style={fieldStyle}>
        <label style={labelStyle}>퇴근 시간 (HH:mm)</label>
        <input
          style={compactInput} type="text" inputMode="numeric" placeholder="1810 또는 18:10"
          value={form.check_out_time}
          onChange={(e) => patchTimeForm('check_out_time', e.target.value)}
          onBlur={(e) => patchTimeForm('check_out_time', finalizeTime(e.target.value))}
        />
      </div>

      {/* 퇴근 IP */}
      <div style={fieldStyle}>
        <label style={labelStyle}>퇴근 IP</label>
        <input
          style={compactInput} value={form.check_out_ip_address}
          onChange={(e) => setForm({ ...form, check_out_ip_address: e.target.value })}
        />
      </div>

      {/* 퇴근 주소 — 전체 폭 */}
      <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
        <label style={labelStyle}>퇴근 주소</label>
        <input
          style={compactInput} value={form.check_out_address}
          onChange={(e) => setForm({ ...form, check_out_address: e.target.value })}
        />
      </div>

      {/* 휴게 시간 (빈 값 입력 시 자동 계산으로 리셋) */}
      <div style={fieldStyle}>
        <label style={labelStyle}>휴게시간 (분)</label>
        <input
          style={compactInput} type="number" min="0" max="600"
          value={form.break_minutes}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') {
              // 빈 값 → 자동 계산으로 복원
              setBreakManuallyEdited(false);
              setForm((prev) => {
                const total = getTotalMinutesByHHmm(prev.check_in_time, prev.check_out_time);
                return { ...prev, break_minutes: total === null ? '' : String(getAutoBreakMinutes(total)) };
              });
              return;
            }
            setBreakManuallyEdited(true);
            setForm((prev) => ({ ...prev, break_minutes: v }));
          }}
        />
      </div>

      {/* 근무시간 계산 미리보기 — 전체 폭 */}
      <div style={{ ...registeredBoxStyle, gridColumn: '1 / -1', padding: '6px 10px' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>근무시간 계산 미리보기</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {(() => {
            const total = getTotalMinutesByHHmm(form.check_in_time, form.check_out_time);
            if (total === null) return '출근/퇴근 시간을 입력하면 근무/휴게시간이 자동 계산됩니다.';
            const fallbackBreak = getAutoBreakMinutes(total);
            const breakMin = form.break_minutes === '' ? fallbackBreak : Number(form.break_minutes || 0);
            const workMin  = Math.max(0, total - (Number.isFinite(breakMin) ? breakMin : 0));
            return `근무시간 ${formatMinutesKorean(workMin)} / 휴게시간 ${formatMinutesKorean(Number.isFinite(breakMin) ? breakMin : 0)} (자동 기준: 4시간↑ 30분, 6시간30분↑ 120분)`;
          })()}
        </div>
      </div>

      {/* 해당 직원의 등록 IP 목록 참고용 — 전체 폭 */}
      <div style={{ ...registeredBoxStyle, gridColumn: '1 / -1', padding: '6px 10px' }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>등록된 IP 주소</div>
        {editingRegisteredIps.length === 0 ? (
          <div style={{ color: 'var(--text-muted)' }}>등록된 IP가 없습니다.</div>
        ) : (
          <ul style={registeredListStyle}>
            {editingRegisteredIps.map((ipRow) => (
              <li key={ipRow.id}>{formatIpWithAlias(ipRow.ip_address, ipRow.label)}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
